import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from '../ConfirmDialog';
import { updateFamily } from '../../lib/formulas';

const DATE = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' });

// One row per formula, grouped by product type.
//
// What a row shows was cut back hard: batch size, batch cost, total % and
// "updated by" were all on it before, and none of them are browsing facts.
// Total % is a validation state (it belongs in the editor) and cost was a
// nag reading "Cost unavailable" on every card while nothing was priced.
export default function FormulaList({ formulas, batches, productTypes, loading, onAdd, onOpen, onChanged }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [query, setQuery] = useState('');
  const [typeId, setTypeId] = useState('All');
  const [showArchived, setShowArchived] = useState(false);
  const [menuFor, setMenuFor] = useState(null);

  // Last made, per formula, from batches already loaded by the page.
  const lastMade = useMemo(() => {
    const map = new Map();
    for (const batch of batches || []) {
      const familyId = batch.formula?.family_id;
      const when = batch.date_completed || batch.made_on;
      if (!familyId || !when) continue;
      if (!map.has(familyId) || when > map.get(familyId)) map.set(familyId, when);
    }
    return map;
  }, [batches]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (formulas || []).filter((f) => {
      if (!showArchived && f.status === 'Archived') return false;
      if (typeId !== 'All' && f.product_type_id !== typeId) return false;
      if (!needle) return true;
      return (
        f.name.toLowerCase().includes(needle) ||
        (f.activeVariants || []).some((v) => v.name.toLowerCase().includes(needle))
      );
    });
  }, [formulas, query, typeId, showArchived]);

  // Grouped by product type, in the product types' own order.
  const groups = useMemo(() => {
    const order = new Map((productTypes || []).map((t, i) => [t.id, i]));
    const byType = new Map();
    for (const formula of filtered) {
      const key = formula.product_type?.name || formula.product_type || 'Other';
      if (!byType.has(key)) byType.set(key, { key, sort: order.get(formula.product_type_id) ?? 99, rows: [] });
      byType.get(key).rows.push(formula);
    }
    return [...byType.values()].sort((a, b) => a.sort - b.sort || a.key.localeCompare(b.key));
  }, [filtered, productTypes]);

  const handleArchive = async (formula) => {
    const archiving = formula.status !== 'Archived';
    if (
      archiving &&
      !(await confirm({
        title: `Archive ${formula.name}?`,
        body: 'It disappears from the list but keeps every version and batch it produced. You can bring it back at any time.',
      }))
    )
      return;
    await updateFamily(formula.id, { status: archiving ? 'Archived' : 'Active' });
    setMenuFor(null);
    await onChanged();
  };

  return (
    <>
      <div className="lab-toolbar">
        <input
          className="lab-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search formulas and variants"
        />
        <select value={typeId} onChange={(e) => setTypeId(e.target.value)} className="lab-select">
          <option value="All">All product types</option>
          {(productTypes || []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <label className="lab-checkbox">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Archived
        </label>
        <button className="primary-action" onClick={onAdd}>
          + New formula
        </button>
      </div>

      {loading ? (
        <p className="formula-loading">Loading…</p>
      ) : groups.length === 0 ? (
        <div className="lab-empty">
          <p>
            {formulas.length === 0
              ? "No formulas yet. Add one and it'll be ready to batch from."
              : 'Nothing matches those filters.'}
          </p>
          {formulas.length === 0 && (
            <button className="primary-action" onClick={onAdd}>
              + New formula
            </button>
          )}
        </div>
      ) : (
        groups.map((group) => (
          <section className="lab-group" key={group.key}>
            <h3 className="lab-group-head">{group.key}</h3>
            <ul className="lab-rows">
              {group.rows.map((formula) => {
                const made = lastMade.get(formula.id);
                const variants = formula.activeVariants?.length || 0;
                return (
                  <li className="lab-row" key={formula.id}>
                    <button className="lab-row-main" onClick={() => onOpen(formula)}>
                      <span className="lab-row-name">{formula.name}</span>
                      {variants > 0 && (
                        <span className="lab-row-variants">
                          {variants} variant{variants === 1 ? '' : 's'}
                        </span>
                      )}
                      {formula.status === 'Archived' && (
                        <span className="lab-row-archived">Archived</span>
                      )}
                      <span className="lab-row-meta">
                        {formula.currentVersion ? `v${formula.currentVersion.version_number}` : 'No version'}
                      </span>
                      <span className="lab-row-meta">
                        {made ? `Last made ${DATE.format(new Date(`${made}T12:00:00`))}` : 'Never made'}
                      </span>
                    </button>

                    <div className="lab-row-menu">
                      <button
                        className="lab-row-dots"
                        aria-label={`Actions for ${formula.name}`}
                        onClick={() => setMenuFor(menuFor === formula.id ? null : formula.id)}
                      >
                        ···
                      </button>
                      {menuFor === formula.id && (
                        <>
                          {/* Click-away closer, so the menu can't be left
                              open behind whatever you press next. */}
                          <div className="lab-menu-scrim" onClick={() => setMenuFor(null)} />
                          <ul className="lab-menu">
                            <li>
                              <button onClick={() => navigate(`/production?make=${formula.id}`)}>
                                Make a batch
                              </button>
                            </li>
                            <li>
                              <button onClick={() => onOpen(formula)}>Open</button>
                            </li>
                            <li>
                              <button onClick={() => handleArchive(formula)}>
                                {formula.status === 'Archived' ? 'Restore' : 'Archive'}
                              </button>
                            </li>
                          </ul>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </>
  );
}
